import { forwardRef } from 'react';
import classnames from 'classnames';
import { CasesBoardColumnHeader } from '@lp/features/CasesBoard/components/CasesBoardColumnHeader';
import { EditDraftCaseModal } from '@lp/features/modals/EditDraftCaseModal/EditDraftCaseModal';
import { Skeleton } from '@ds';
import { orderBy, times } from 'lodash-es';
import { StatusGroup, useCasesBoardStore } from '@/store/cases-board.store';
import { CaseStatus } from '@/api/apis/case';
import styles from './CasesBoardGroup.module.scss';
import { useCasesByRowInfiniteQuery } from '@/queries/case';
import { mergeRefs } from '@/utils/helpers';
import { CaseCard } from '../CaseCard';
import { useRowSettle } from './hooks/useRowSettle';
import { useCaseCardActions } from './hooks/useCaseCardActions';
import { useLoadMoreOnScroll } from './hooks/useLoadMoreOnScroll';

const CARD_SKELETON_CLASS = 'min-h-[250px] min-w-[292px] rounded-lg';

const CardSkeletons = ({ disableAnimation }: { disableAnimation?: boolean }) => (
  <>
    {times(5, (n) => (
      <Skeleton key={n} disableAnimation={disableAnimation} className={CARD_SKELETON_CLASS} />
    ))}
  </>
);

export type CasesBoardGroupProps = {
  isRows: boolean;
  status?: CaseStatus;
  group?: StatusGroup;
  title: string;
};

export const CasesBoardGroup = forwardRef<HTMLDivElement, CasesBoardGroupProps>(
  ({ status, group, title, isRows }, ref) => {
    const hideEmpty = useCasesBoardStore((store) => store.hideEmpty);
    const { settled, ref: settleRef } = useRowSettle();

    const { data, count, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useCasesByRowInfiniteQuery({
      status,
      group,
      enabled: settled,
    });

    const { draftEditModalCase, onCardClick, onDuplicateClick, closeDraftEditModal } = useCaseCardActions();
    const { ref: loadMoreRef } = useLoadMoreOnScroll(hasNextPage, isFetchingNextPage, fetchNextPage);

    if (hideEmpty && data && !data.length) return null;

    const casesOrdered = orderBy(data, (c) => c.createdAt, 'desc');
    const isEmptyRow = isRows && !isLoading && data?.length === 0;

    const showCards = !isLoading && settled && data;

    return (
      <div
        className={classnames(isRows ? styles.row : styles.column, 'scroll scroll-lg')}
        ref={mergeRefs(ref, settleRef)}
      >
        {draftEditModalCase && <EditDraftCaseModal caseInfo={draftEditModalCase} onClose={closeDraftEditModal} />}
        <CasesBoardColumnHeader isRows={isRows} title={title} amount={count} />
        <div
          className={classnames(styles.cardsList, { [styles.cardsListEmpty]: isEmptyRow })}
          data-testid="cases-board-cards-list"
        >
          {isLoading && <CardSkeletons />}
          {!isLoading && !showCards && <CardSkeletons disableAnimation />}
          {showCards && (
            <>
              {casesOrdered.map((c) => (
                <CaseCard key={c.caseId} caseData={c} onClick={onCardClick(c)} onDuplicateClick={onDuplicateClick} />
              ))}
              <div ref={loadMoreRef} className="min-h-px" />
            </>
          )}
          {isFetchingNextPage && <Skeleton className={CARD_SKELETON_CLASS} />}
        </div>
      </div>
    );
  },
);
